import React, { useCallback } from "react";
import { Modal, Button, App } from "antd";
import { useTranslation } from "react-i18next";
import OfferSectionBlock from "./OfferSectionBlock";
import { buildMarketingOfferWhatsappText } from "../../../../utils/dynamicPackage/generateMarketingOffer";
import { copyTextToClipboard } from "../../../../utils/copyTextToClipboard";
import "./MarketingOfferPreviewModal.css";

export function FlightDirectionBlock({ row }) {
  const hasContent =
    row?.hasLeg ||
    row?.calendarDate ||
    row?.timeRange ||
    row?.routeLine ||
    row?.airline;

  return (
    <div className="moffer-flight-row">
      <div className="moffer-flight-row__label">{row?.labelHe || "—"}</div>
      {!hasContent ? (
        <p className="moffer-flight-row__muted">לא נבחרה</p>
      ) : (
        <dl className="moffer-flight-row__dl">
          {row.calendarDate ? (
            <>
              <dt>תאריך</dt>
              <dd>{row.calendarDate}</dd>
            </>
          ) : null}
          {row.timeRange ? (
            <>
              <dt>שעות</dt>
              <dd>{row.timeRange}</dd>
            </>
          ) : null}
          {row.routeLine ? (
            <>
              <dt>מסלול</dt>
              <dd>{row.routeLine}</dd>
            </>
          ) : null}
          {row.airline ? (
            <>
              <dt>חברת תעופה</dt>
              <dd>{row.airline}</dd>
            </>
          ) : null}
          {row.directLabel ? (
            <>
              <dt>סוג</dt>
              <dd>{row.directLabel}</dd>
            </>
          ) : null}
        </dl>
      )}
    </div>
  );
}

/**
 * תצוגה ויזואלית של הצעה + העתקת טקסט לוואטסאפ
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object|null} props.viewModel — מ־buildMarketingOfferViewModel
 */
export default function MarketingOfferPreviewModal({ open, onClose, viewModel }) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const handleCopy = useCallback(async () => {
    if (!viewModel) return;
    const text = buildMarketingOfferWhatsappText(viewModel);
    const ok = await copyTextToClipboard(text);
    if (ok) {
      message.success(t("marketingOffer.copied", "ההצעה הועתקה"));
    } else {
      message.error(t("marketingOffer.copyFailed", "לא ניתן להעתיק — נסו שוב"));
    }
  }, [message, t, viewModel]);

  if (!viewModel) return null;

  const h = viewModel.header;
  const f = viewModel.flights;
  const hot = viewModel.hotel;
  const r = viewModel.room;
  const p = viewModel.price;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      centered
      destroyOnClose
      className="moffer-preview-modal"
      title={null}
    >
      <div className="moffer-preview" dir="rtl">
        {viewModel.heroImageUrl ? (
          <div className="moffer-preview__hero">
            <img
              src={viewModel.heroImageUrl}
              alt=""
              className="moffer-preview__hero-img"
              loading="lazy"
            />
          </div>
        ) : null}

        <header className="moffer-preview__header">
          <p className="moffer-preview__kicker">{h.packageTaglineHe}</p>
          <h2 className="moffer-preview__headline">{h.headlineHe}</h2>
          <p className="moffer-preview__dates">
            <span className="moffer-preview__dates-icon" aria-hidden>
              📅
            </span>
            {h.datesLine}
          </p>
        </header>

        <OfferSectionBlock title="טיסות">
          <p className="moffer-preview__stay-note">{f.stayDatesNoteHe}</p>
          <FlightDirectionBlock row={f.outbound} />
          {f.showInboundSection ? (
            <FlightDirectionBlock row={f.inbound} />
          ) : (
            <p className="moffer-flight-row__muted moffer-flight-row__muted--block">
              חבילת כיוון אחד — ללא טיסת חזור
            </p>
          )}
          {f.baggageLine ? (
            <p className="moffer-preview__baggage">כבודה: {f.baggageLine}</p>
          ) : null}
        </OfferSectionBlock>

        <OfferSectionBlock title="מלון">
          <div className="moffer-hotel-block">
            <div className="moffer-hotel-block__title-row">
              <span className="moffer-hotel-block__name">{hot.name}</span>
              {hot.starsCount > 0 ? (
                <span className="moffer-hotel-block__stars" aria-hidden>
                  {hot.starsEmoji}
                </span>
              ) : null}
            </div>
            <p className="moffer-hotel-block__line">
              <span className="moffer-hotel-block__icon">📍</span>
              {hot.locationLine}
            </p>
            <p className="moffer-hotel-block__line">
              <span className="moffer-hotel-block__icon">🍽️</span>
              בסיס אירוח: {hot.boardLabel}
            </p>
            <ul className="moffer-hotel-block__facilities">
              {hot.facilities.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        </OfferSectionBlock>

        <OfferSectionBlock title="חדר נבחר">
          <p className="moffer-room-name">{r.name}</p>
          <p className="moffer-room-pax">מתאים ל: {r.occupancyLabel}</p>
        </OfferSectionBlock>

        <OfferSectionBlock title="מחיר" variant="price">
          <div className="moffer-price-block">
            <div className="moffer-price-block__label">{p.titleHe}</div>
            <div className="moffer-price-block__amount">{p.amountDisplay}</div>
            <div className="moffer-price-block__sub">{p.subTitleHe}</div>
            <p className="moffer-price-block__disclaimer">{p.disclaimer}</p>
          </div>
        </OfferSectionBlock>

        <p className="moffer-preview__footer-cta">{viewModel.footerCtaHe}</p>

        <div className="moffer-preview__actions">
          <Button type="primary" size="large" block onClick={handleCopy}>
            {t("marketingOffer.copyToClient", "העתק ללקוח")}
          </Button>
          <Button size="large" block onClick={onClose}>
            {t("marketingOffer.closePreview", "סגור")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
